import { dpr } from 'Utils/dpr';
import tailwindBreakpoints from 'Utils/tailwindBreakpoints';

import { IImageBaseViewModel } from 'Types/Litium';

export interface ISrcSetSize {
    default: { width: number; height: number };
    md?: { width: number; height: number };
    lg: { width: number; height: number };
    xl: { width: number; height: number };
    xxl: { width: number; height: number };
    xxxl?: { width: number; height: number };
}

type SizeEntries<T> = {
    [K in keyof T]: [K, T[K]];
}[keyof T][];

const ProductCardImage = ({
    image,
    srcSetSizes,
    className,
    lazy,
    isSoldOut,
    evenAspectRatio,
}: {
    image: IImageBaseViewModel;
    srcSetSizes: ISrcSetSize;
    className: string;
    lazy?: boolean;
    isSoldOut: boolean;
    evenAspectRatio?: boolean;
}) => {
    const reduceToDoubleDigits = (num: number) =>
        num >= 10 ? parseInt(num.toString().substring(0, 2)) : num;

    const sizes = (
        Object.entries(srcSetSizes) as SizeEntries<typeof srcSetSizes>
    )
        .map(([key, value]) => ({
            breakPointVal: parseInt(tailwindBreakpoints[key] as string),
            key,
            width: value.width,
            srcWidth: isSoldOut
                ? reduceToDoubleDigits(value.width)
                : value.width,
            height: value.height,
            srcHeight: isSoldOut
                ? reduceToDoubleDigits(value.height)
                : value.height,
        }))
        .filter(x => tailwindBreakpoints[x.key])
        .sort((a, b) => b.breakPointVal - a.breakPointVal);

    return (
        <picture className={className}>
            {sizes.map(value => (
                <source
                    key={value.key}
                    width={value.width}
                    height={value.height}
                    media={`(min-width: ${tailwindBreakpoints[value.key]})`}
                    srcSet={dpr(
                        `${image?.src}?format=webp&fit=crop&height=${value.srcHeight}&width=${value.srcWidth}`
                    )}
                />
            ))}
            <img
                className={`h-full w-full object-cover ${
                    evenAspectRatio ? 'object-top' : ''
                }`}
                src={`${image?.src}?format=webp&fit=crop&height=${
                    isSoldOut
                        ? reduceToDoubleDigits(srcSetSizes.default.height)
                        : srcSetSizes.default.height
                }&width=${
                    isSoldOut
                        ? reduceToDoubleDigits(srcSetSizes.default.width)
                        : srcSetSizes.default.width
                }`}
                srcSet={dpr(
                    `${image?.src}?format=webp&fit=crop&height=${
                        isSoldOut
                            ? reduceToDoubleDigits(srcSetSizes.default.height)
                            : srcSetSizes.default.height
                    }&width=${
                        isSoldOut
                            ? reduceToDoubleDigits(srcSetSizes.default.width)
                            : srcSetSizes.default.width
                    }`
                )}
                alt={image?.altText}
                loading={lazy ? 'lazy' : 'eager'}
                width={srcSetSizes.default.width}
                height={srcSetSizes.default.height}
            />
        </picture>
    );
};

export default ProductCardImage;
